import { useEffect, useState } from "react";
import api, { shortDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, CheckCircle2, Mail } from "lucide-react";

const STATUSES = ["Not Started", "In Progress", "Waiting", "Completed", "Cancelled"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const canSendDigest = user?.role === "super_admin" || user?.role === "management" || user?.role === "general_manager";

  const load = () => api.get("/tasks").then(r => setTasks(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then(r => setCustomers(r.data));
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/tasks/send-digest");
      if (data.sent > 0) toast.success(`Digest sent to ${data.sent} agent${data.sent !== 1 ? "s" : ""}`);
      else if (data.failed > 0) toast.error(`Failed to send (${data.failed})`);
      else toast.info("No overdue tasks to notify");
    } catch { toast.error("Failed to send digest"); }
    finally { setSending(false); }
  };

  const [remindingDrafts, setRemindingDrafts] = useState(false);
  const remindDrafts = async () => {
    setRemindingDrafts(true);
    try {
      const { data } = await api.post("/visits/remind-drafts", null, { params: { hours: 48 } });
      if (data.sent > 0) toast.success(`Draft reminder sent to ${data.sent} agent${data.sent !== 1 ? "s" : ""} (${data.drafts_found} drafts)`);
      else if (data.drafts_found === 0) toast.info("No stale drafts (>48h)");
      else toast.error(`Failed to send (${data.failed})`);
    } catch { toast.error("Failed to send reminders"); }
    finally { setRemindingDrafts(false); }
  };

  const shown = tasks.filter(t => {
    if (filter === "overdue") return t.is_overdue;
    if (filter === "open") return t.status !== "Completed" && t.status !== "Cancelled";
    return true;
  });

  const update = async (id, patch) => {
    await api.patch(`/tasks/${id}`, patch);
    load();
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Tasks & Follow-ups</h1>
          <p className="text-slate-600 mt-1">{tasks.filter(t => t.is_overdue).length} overdue</p>
        </div>
        <div className="flex gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="tasks-filter">
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="overdue">Overdue</option>
          </select>
          {canSendDigest && (
            <button onClick={sendDigest} disabled={sending} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold flex items-center gap-2 hover:border-[#003DA5] transition" data-testid="send-digest-btn">
              <Mail className="w-4 h-4" />{sending ? "Sending…" : "Send Digest"}
            </button>
          )}
          {canSendDigest && (
            <button onClick={remindDrafts} disabled={remindingDrafts} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold flex items-center gap-2 hover:border-[#003DA5] transition" data-testid="remind-drafts-btn">
              <Mail className="w-4 h-4" />{remindingDrafts ? "Sending…" : "Draft Reminders"}
            </button>
          )}
          <button onClick={() => setShowNew(true)} className="mlily-btn h-11 px-5 rounded-lg font-semibold flex items-center gap-2" data-testid="new-task-btn"><Plus className="w-4 h-4" /> New Task</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
            <th className="text-left px-4 py-3">Task</th>
            <th className="text-left px-4 py-3 hidden md:table-cell">Customer</th>
            <th className="text-left px-4 py-3">Due</th>
            <th className="text-left px-4 py-3">Priority</th>
            <th className="text-left px-4 py-3">Status</th>
          </tr></thead>
          <tbody>{shown.map(t => (
            <tr key={t.id} className={`border-t hover:bg-slate-50 ${t.is_overdue ? "bg-red-50/40" : ""}`} data-testid={`task-row-${t.id}`}>
              <td className="px-4 py-3">
                <div className="font-semibold flex items-center gap-2">
                  {t.status === "Completed" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  {t.title}
                </div>
                <div className="text-xs text-slate-500">{t.description}</div>
              </td>
              <td className="px-4 py-3 hidden md:table-cell text-xs">{customers.find(c => c.id === t.customer_id)?.name || "—"}</td>
              <td className="px-4 py-3">{shortDate(t.due_date)}</td>
              <td className="px-4 py-3"><span className="text-xs badge-info px-2 py-1 rounded-full">{t.priority}</span></td>
              <td className="px-4 py-3">
                <select value={t.status} onChange={(e) => update(t.id, { status: e.target.value })} className="text-xs border rounded px-2 py-1" data-testid={`task-status-${t.id}`}>
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
          {shown.length === 0 && <tr><td colSpan="5" className="px-4 py-16 text-center text-slate-500">No tasks.</td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && <NewTask customers={customers} users={users} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewTask({ customers, users, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", description: "", customer_id: "", assigned_user_id: "", due_date: "", priority: "Medium", status: "Not Started" });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/tasks", f); toast.success("Task created"); onSaved(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg">
        <div className="p-5 border-b flex justify-between"><h3 className="text-xl font-heading font-semibold">New Task</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-4">
          <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Title *</label><input required value={f.title} onChange={upd("title")} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="task-title" /></div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</label><textarea value={f.description} onChange={upd("description")} rows={3} className="mt-1 w-full p-3 border rounded-lg" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</label><select value={f.customer_id} onChange={upd("customer_id")} className="mt-1 w-full h-11 px-3 border rounded-lg"><option value="">—</option>{customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assign To</label><select value={f.assigned_user_id} onChange={upd("assigned_user_id")} className="mt-1 w-full h-11 px-3 border rounded-lg"><option value="">—</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Due Date</label><input type="date" value={f.due_date} onChange={upd("due_date")} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="task-due" /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Priority</label><select value={f.priority} onChange={upd("priority")} className="mt-1 w-full h-11 px-3 border rounded-lg">{["Low", "Medium", "High", "Urgent"].map(p => <option key={p}>{p}</option>)}</select></div>
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button><button type="submit" className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="save-task-btn">Save Task</button></div>
      </form>
    </div>
  );
}
